import React, { useEffect, useState, useRef } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import QRCode from "react-qr-code";
import * as htmlToImage from "html-to-image";
import { useSelector } from "react-redux";
import { Button, Typography, Box, Chip } from "@mui/material";
import logo from "../../../assets/img/marifer.png";

const pdfStyles = StyleSheet.create({
  page: {
    fontSize: "12pt",
    padding: 30,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
    borderBottom: "2px solid #f43f5e",
    paddingBottom: 12,
  },
  logoImg: {
    width: 65,
    height: 65,
    marginRight: 14,
  },
  title: {
    fontSize: 18,
    color: "#f43f5e",
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 10,
    color: "#9ca3af",
  },
  infoBox: {
    backgroundColor: "#fff0f6",
    borderRadius: 6,
    padding: 14,
    marginBottom: 18,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 5,
  },
  label: {
    fontSize: 10,
    color: "#be185d",
    fontFamily: "Helvetica-Bold",
    width: "28%",
  },
  value: {
    fontSize: 10,
    color: "#374151",
    width: "72%",
  },
  qrSection: {
    alignItems: "center",
    marginVertical: 14,
  },
  qrImage: {
    width: 220,
    height: 220,
  },
  qrCaption: {
    marginTop: 8,
    fontSize: 10,
    color: "#6b7280",
    textAlign: "center",
  },
  footer: {
    marginTop: 20,
    borderTop: "1px solid #fce7f3",
    paddingTop: 7,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 8,
    color: "#f472b6",
  },
});

const ImpresionQrPdfInsumos = () => {
  const qrRef = useRef(null);
  const [data64, setBase64] = useState(null);
  const { defaultData } = useSelector(({ insumo }) => insumo);

  const nombre = defaultData?.nombre || "";
  const codigo = defaultData?.codigo || "";
  const descripcion = defaultData?.descripcion || "";
  const presentacion = defaultData?.details?.presentacion || "";
  const unidad = defaultData?.unit_base || "";
  const insumoId = defaultData?.id;

  const qrValue = JSON.stringify({ insumoId, nombre, codigo });

  useEffect(() => {
    if (!qrRef.current) return;
    const capture = async () => {
      const img = await htmlToImage.toPng(qrRef.current);
      setBase64(img);
    };
    const t = setTimeout(capture, 300);
    return () => clearTimeout(t);
  }, [insumoId]);

  const PdfDocument = () => {
    if (!data64) return null;
    return (
      <Document>
        <Page style={pdfStyles.page}>
          <View>
            <View style={pdfStyles.header}>
              <Image style={pdfStyles.logoImg} src={logo} />
              <View>
                <Text style={pdfStyles.title}>Habilitado — Código QR</Text>
                <Text style={pdfStyles.subtitle}>Sistema de gestión Marifer</Text>
              </View>
            </View>

            <View style={pdfStyles.infoBox}>
              <View style={pdfStyles.infoRow}>
                <Text style={pdfStyles.label}>Nombre:</Text>
                <Text style={pdfStyles.value}>{nombre}</Text>
              </View>
              {!!codigo && (
                <View style={pdfStyles.infoRow}>
                  <Text style={pdfStyles.label}>Código:</Text>
                  <Text style={pdfStyles.value}>{codigo}</Text>
                </View>
              )}
              {!!descripcion && (
                <View style={pdfStyles.infoRow}>
                  <Text style={pdfStyles.label}>Descripción:</Text>
                  <Text style={pdfStyles.value}>{descripcion}</Text>
                </View>
              )}
              {!!presentacion && (
                <View style={pdfStyles.infoRow}>
                  <Text style={pdfStyles.label}>Presentación:</Text>
                  <Text style={pdfStyles.value}>{presentacion}</Text>
                </View>
              )}
              {!!unidad && (
                <View style={pdfStyles.infoRow}>
                  <Text style={pdfStyles.label}>Unidad:</Text>
                  <Text style={pdfStyles.value}>{unidad}</Text>
                </View>
              )}
            </View>

            <View style={pdfStyles.qrSection}>
              <Image style={pdfStyles.qrImage} src={data64} />
              <Text style={pdfStyles.qrCaption}>
                Escanea para identificar este habilitado en el sistema
              </Text>
            </View>

            <View style={pdfStyles.footer}>
              <Text style={pdfStyles.footerText}>
                ©Marifer {new Date().getFullYear()}
              </Text>
              <Text style={pdfStyles.footerText}>
                Generado: {new Date().toLocaleDateString("es-MX")}
              </Text>
            </View>
          </View>
        </Page>
      </Document>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, py: 2 }}>
      {/* Información del insumo */}
      <Box sx={{ width: "100%", maxWidth: 400, background: "#fff0f6", border: "1px solid #fce7f3", borderRadius: 2, p: 2 }}>
        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1rem", color: "#be185d", mb: 1 }}>
          {nombre}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
          {!!codigo && (
            <Chip label={`Código: ${codigo}`} size="small"
              sx={{ fontFamily: "'Poppins', sans-serif", background: "#fce7f3", color: "#be185d" }} />
          )}
          {!!unidad && (
            <Chip label={`Unidad: ${unidad}`} size="small"
              sx={{ fontFamily: "'Poppins', sans-serif", background: "#fce7f3", color: "#be185d" }} />
          )}
        </Box>
        {!!descripcion && (
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem", color: "#6b7280", mt: 1 }}>
            {descripcion}
          </Typography>
        )}
      </Box>

      {/* QR visual */}
      <Box
        ref={qrRef}
        sx={{ background: "#fff", p: 2, borderRadius: 2, border: "1px solid #fce7f3", boxShadow: "0 4px 16px rgba(244,63,94,0.12)" }}
      >
        <QRCode
          value={qrValue}
          size={220}
          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
          viewBox="0 0 256 256"
        />
      </Box>

      {/* Botones de descarga */}
      {data64 ? (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
          {/* Descargar imagen PNG del QR */}
          <Button
            variant="outlined"
            onClick={() => {
              const link = document.createElement("a");
              link.download = `QR_Habilitado_${nombre || insumoId}.png`;
              link.href = data64;
              link.click();
            }}
            sx={{
              borderColor: "#f43f5e",
              color: "#f43f5e",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": { borderColor: "#e11d48", background: "#fff0f6" },
            }}
          >
            Descargar imagen QR (PNG)
          </Button>

          {/* Descargar PDF */}
          <PDFDownloadLink
            document={<PdfDocument />}
            fileName={`QR_Habilitado_${nombre || insumoId}.pdf`}
          >
            {({ loading }) =>
              loading ? (
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: "#f472b6", fontSize: "0.85rem" }}>
                  Generando PDF...
                </Typography>
              ) : (
                <Button
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: "0 4px 14px rgba(244,63,94,0.35)",
                    "&:hover": { background: "linear-gradient(135deg, #e11d48 0%, #db2777 100%)" },
                  }}
                >
                  Descargar Código QR (PDF)
                </Button>
              )
            }
          </PDFDownloadLink>
        </Box>
      ) : (
        <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: "#9ca3af", fontSize: "0.8rem" }}>
          Capturando QR...
        </Typography>
      )}
    </Box>
  );
};

export default ImpresionQrPdfInsumos;

